import React from "react";
import { useTranslation } from "react-i18next";

import { AMENITY_CONSTANTS } from "@constants/amenities";
import { CONSTANTS } from "@constants/index";
import { BookedAmenitiesProps } from "@models/components/amenity.model";
import { BookedAmenityType } from "@models/components/user.model";
import { applyLocalization } from "@services/common.service";
import { formatPrice } from "@utils/common.utils";

const BookedAmenities: React.FC<BookedAmenitiesProps> = ({
  bookingDetails,
}) => {
  const { t } = useTranslation();
  const amenities = bookingDetails?.amenities;

  if (!amenities || Object.keys(amenities).length === CONSTANTS.DEFAULT_VALUE)
    return null;

  return (
    <div className="fg-booked-amenities-wrapper mt-3">
      {/* Dashed top border */}
      <div className="fg-dashed-divider" />

      <h6 className="fg-summary-section-header mb-2">{t("AMENITIES")}</h6>

      <div className="fg-amenities-list">
        {(() => {
          const consolidated = (amenities as BookedAmenityType[]).reduce<
            [string, BookedAmenityType][]
          >((acc, amenity) => {
            const unitPrice =
              amenity.quantity > 0 ? amenity.totalPrice / amenity.quantity : 0;
            const existing = acc.find(([, a]) => {
              const aUnit = a.quantity > 0 ? a.totalPrice / a.quantity : 0;
              return (
                a.id === amenity.id &&
                Math.abs(aUnit - unitPrice) < 0.01 &&
                a.vatPercentage === amenity.vatPercentage
              );
            });
            if (existing) {
              existing[1] = {
                ...existing[1],
                quantity: (existing[1].quantity ?? 0) + (amenity.quantity ?? 0),
                totalPrice:
                  (existing[1].totalPrice ?? 0) + (amenity.totalPrice ?? 0),
              };
            } else {
              acc.push([amenity.id, { ...amenity }]);
            }
            return acc;
          }, []);

          return consolidated.map(([, amenity], index) => {
            const vatPercentageNum = Number.parseFloat(
              amenity.vatPercentage?.toString().replace("%", "") ||
                AMENITY_CONSTANTS.DEFAULT_VAT
            );

            const badgeClass =
              vatPercentageNum > CONSTANTS.VAT_STANDARD_RATE
                ? "fg-vat-high"
                : "fg-vat-standard";

            const isLast = index === consolidated.length - 1;

            return (
              <React.Fragment key={`${amenity.id}-${index}`}>
                <div className="d-flex align-items-center justify-content-between mb-2 mt-2">
                  <div className="d-flex align-items-center flex-wrap gap-2">
                    <div className="fg-FS-14 fg-text-primary font-weight-light">
                      {applyLocalization(amenity.title)}
                      {amenity.quantity !== undefined &&
                        ` \u00D7 ${amenity.quantity}`}
                    </div>
                    {vatPercentageNum > 0 && !!amenity.totalPrice && (
                      <span
                        className={`badge rounded-pill fg-FS-10 px-2 py-1 fg-vat-badge ${badgeClass}`}
                      >
                        {amenity.vatPercentage.includes("%")
                          ? amenity.vatPercentage
                          : `${amenity.vatPercentage}%`}
                      </span>
                    )}
                  </div>
                  <div className="text-end">
                    <div className="fg-FS-14 fg-text-primary font-weight-light">
                      {formatPrice(
                        amenity.totalPrice,
                        bookingDetails?.currency || ""
                      )}
                    </div>
                  </div>
                </div>
                {!isLast && <div className="fg-amenity-item-divider" />}
              </React.Fragment>
            );
          });
        })()}
      </div>

      <div className="fg-dashed-divider" />
    </div>
  );
};

export default BookedAmenities;
